export const LAW_TYPES = ['GST', 'Income Tax', 'Other'];

export const MATTER_TEMPLATES = [
  { value: 'standard', label: 'Standard Notice / Appeal Matter' },
  { value: 'gst_refund', label: 'GST Refund Matter' },
];

export const GST_REFUND_STAGES = [
  'To Be Filed',
  'Application Filed',
  'Acknowledged / Deficiency Memo',
  'Refiled',
  'SCN Received',
  'Reply Filed',
  'Sanction Order Received',
  'Refund Credited',
  'Closed',
];

export const GST_REFUND_CATEGORIES = [
  'Inverted Duty Structure',
  'Export with payment of tax',
  'Export without payment of tax',
  'Deemed Export',
  'Excess balance in Electronic Cash Ledger',
  'Refund arising from order / appeal',
  'Supplies to SEZ',
  'Other Refund',
];

export const GST_REFUND_OUTCOMES = ['Pending', 'Fully Sanctioned', 'Partly Sanctioned', 'Rejected', 'Withdrawn'];

export const GST_STAGES = [
  'Scrutiny – ASMT-10',
  'Adjudication – DRC-01',
  'First Appeal',
  'Tribunal',
  'High Court',
  'Supreme Court',
  'Other',
];

export const INCOME_TAX_STAGES = [
  'Scrutiny / Assessment',
  'Reassessment',
  'Rectification',
  'Revision',
  'Penalty Proceedings',
  'First Appeal – CIT(A)',
  'Tribunal – ITAT',
  'High Court',
  'Supreme Court',
  'Other',
];

export const OTHER_STAGES = [
  'Original Proceeding',
  'First Appeal',
  'Tribunal',
  'High Court',
  'Supreme Court',
  'Other',
];

export const MATTER_STATUSES = [
  'Open',
  'Pending with Client',
  'Pending with Department',
  'Under Hearing',
  'Order Received',
  'Appeal Pending',
  'Closed',
  'Archived',
];

export const ACTIVE_MATTER_STATUSES = [
  'Open',
  'Pending with Client',
  'Pending with Department',
  'Under Hearing',
  'Appeal Pending',
];

export const CASE_EVENT_TYPES = [
  'Notice Issued',
  'Notice Received',
  'Reply Due',
  'Reply Filed',
  'Hearing Notice Issued',
  'Hearing Scheduled',
  'Hearing Attended',
  'Hearing Not Attended',
  'Adjournment Requested',
  'Adjournment Granted',
  'Next Hearing Fixed',
  'Documents Required',
  'Additional Evidence Required',
  'Additional Evidence Filed',
  'Additional Documents Filed',
  'Paper Book Filed',
  'Written Submission Filed',
  'Order Passed',
  'Appeal Recommended',
  'Appeal Filed',
  'Stage Transition',
  'Matter Closed',
  'Matter Reopened',
  'General Update',
];

export const ORDER_OUTCOMES = ['In Favour', 'Partly in Favour', 'Against', 'Remanded', 'Proceedings Dropped'];
export const HEARING_MODES = ['Physical', 'Video Conference', 'Faceless', 'Hybrid', 'Not Applicable'];

export function stagesForLaw(lawType, matterTemplate = '') {
  if (matterTemplate === 'gst_refund') return GST_REFUND_STAGES;
  if (lawType === 'GST') return GST_STAGES;
  if (lawType === 'Income Tax') return INCOME_TAX_STAGES;
  return OTHER_STAGES;
}

export function inferLawTypeFromAssignmentType(value, fallback = 'Other') {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return fallback;
  if (text.includes('income tax') || text.includes('income-tax') || text.includes('itat') || text.includes('cit(a)')) return 'Income Tax';
  if (text.includes('gst') || text.includes('asmt') || text.includes('drc') || text.includes('gstat')) return 'GST';
  return fallback;
}

export function initialStageForMatterType(value, lawType, matterTemplate = '') {
  if (matterTemplate === 'gst_refund' || String(value || '').toLowerCase().includes('gst refund')) return GST_REFUND_STAGES[0];
  const text = String(value || '').trim().toLowerCase();
  const resolvedLaw = lawType || inferLawTypeFromAssignmentType(value, 'Other');

  if (resolvedLaw === 'Income Tax') {
    if (text.includes('tribunal') || text.includes('itat')) return 'Tribunal – ITAT';
    if (text.includes('appeal') || text.includes('cit(a)')) return 'First Appeal – CIT(A)';
    if (text.includes('reassessment') || text.includes('148')) return 'Reassessment';
    if (text.includes('penalty')) return 'Penalty Proceedings';
    if (text.includes('rectification') || text.includes('154')) return 'Rectification';
    if (text.includes('revision') || text.includes('263') || text.includes('264')) return 'Revision';
    return 'Scrutiny / Assessment';
  }

  if (resolvedLaw === 'GST') {
    if (text.includes('tribunal') || text.includes('gstat')) return 'Tribunal';
    if (text.includes('appeal')) return 'First Appeal';
    if (text.includes('drc-01') || text.includes('drc 01') || text.includes('adjudication')) return 'Adjudication – DRC-01';
    return 'Scrutiny – ASMT-10';
  }

  if (text.includes('tribunal')) return 'Tribunal';
  if (text.includes('appeal')) return 'First Appeal';
  return 'Original Proceeding';
}

export function isStageCompatibleWithLaw(stage, lawType, matterTemplate = '') {
  return !stage || stagesForLaw(lawType, matterTemplate).includes(stage);
}

export function isGstRefundMatter(matter = {}) {
  return matter.matterTemplate === 'gst_refund' || String(matter.assignmentType || matter.title || '').toLowerCase().includes('gst refund');
}

export function suggestedMatterClassification(matter = {}) {
  const source = matter.assignmentType || matter.title || '';
  const inferredLaw = inferLawTypeFromAssignmentType(source, matter.lawType || 'Other');
  const currentLaw = LAW_TYPES.includes(matter.lawType) ? matter.lawType : inferredLaw;
  const lawType = inferredLaw !== 'Other' ? inferredLaw : currentLaw;
  const matterTemplate = matter.matterTemplate || (String(source).toLowerCase().includes('gst refund') ? 'gst_refund' : 'standard');
  const currentStage = isStageCompatibleWithLaw(matter.currentStage, lawType, matterTemplate)
    ? (matter.currentStage || initialStageForMatterType(source, lawType, matterTemplate))
    : initialStageForMatterType(source, lawType, matterTemplate);

  return {
    lawType,
    currentStage,
    needsCorrection: lawType !== matter.lawType || currentStage !== matter.currentStage,
  };
}

export const EMPTY_MATTER = {
  clientId: '', assignmentId: '', assignmentType: '', financialYear: '', period: '', assignedToId: '',
  title: '', matterTemplate: 'standard', lawType: 'GST', currentStage: GST_STAGES[0], noticeNumber: '', authority: '',
  noticeReceivedDate: '', replyDueDate: '', replyFiledDate: '', hearingDate: '', nextHearingDate: '',
  appealDueDate: '', orderReceivedDate: '', status: 'Open', currentStatus: '', latestDevelopment: '',
  nextAction: '', nextActionDate: '', remarks: '', closureDate: '', finalOutcome: '', closureRemarks: '', orderReference: '',
  templateId:'', templateKey:'', templateName:'', templateVersion:1, templateSnapshot:null, customFieldValues:{}, clientVisible:false, clientStatus:'', clientUpdate:'',
  refundCategory: '', refundClaimAmount: '', refundApplicationDate: '', refundArn: '', acknowledgementStatus: '', acknowledgementDate: '', acknowledgementNumber: '', deficiencyMemoDate: '', deficiencyMemoNumber: '', previousArn: '', refiledDate: '', scnNumber: '', scnDate: '', proposedDeductionAmount: '', refundReplyDueDate: '', refundReplyFiledDate: '', sanctionOrderDate: '', sanctionOrderNumber: '', sanctionedAmount: '', refundOutcome: 'Pending', refundCreditedDate: '', amountCredited: '', interestCredited: '', bankReference: '',
};

export function toInputDate(value) {
  if (!value) return '';
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatMatterDate(value) {
  if (!value) return '—';
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-IN');
}

export function normalizeMatterForForm(matter) {
  const safe = matter || {};
  const classification = suggestedMatterClassification(safe);
  return {
    ...EMPTY_MATTER,
    ...safe,
    matterTemplate: safe.matterTemplate || (String(safe.assignmentType || safe.title || '').toLowerCase().includes('gst refund') ? 'gst_refund' : 'standard'),
    lawType: classification.lawType,
    currentStage: classification.currentStage,
    noticeReceivedDate: toInputDate(safe.noticeReceivedDate), replyDueDate: toInputDate(safe.replyDueDate),
    replyFiledDate: toInputDate(safe.replyFiledDate), hearingDate: toInputDate(safe.hearingDate),
    nextHearingDate: toInputDate(safe.nextHearingDate), appealDueDate: toInputDate(safe.appealDueDate),
    orderReceivedDate: toInputDate(safe.orderReceivedDate), nextActionDate: toInputDate(safe.nextActionDate),
    closureDate: toInputDate(safe.closureDate),
    refundApplicationDate: toInputDate(safe.refundApplicationDate), acknowledgementDate: toInputDate(safe.acknowledgementDate), deficiencyMemoDate: toInputDate(safe.deficiencyMemoDate), refiledDate: toInputDate(safe.refiledDate), scnDate: toInputDate(safe.scnDate), refundReplyDueDate: toInputDate(safe.refundReplyDueDate), refundReplyFiledDate: toInputDate(safe.refundReplyFiledDate), sanctionOrderDate: toInputDate(safe.sanctionOrderDate), refundCreditedDate: toInputDate(safe.refundCreditedDate),
  };
}

export function validateMatter(values) {
  const errors = {};
  if (!values.clientId) errors.clientId = 'Client is required.';
  if (!values.title?.trim()) errors.title = 'Matter title is required.';
  if (!LAW_TYPES.includes(values.lawType)) errors.lawType = 'Select a valid law type.';
  if (!values.currentStage) errors.currentStage = 'Proceeding stage is required.';
  if (values.matterTemplate === 'gst_refund' && values.currentStage !== 'To Be Filed') {
    if (!values.refundCategory) errors.refundCategory = 'Refund category is required from Application Filed stage onwards.';
    if (!Number(values.refundClaimAmount || 0)) errors.refundClaimAmount = 'Claim amount is required from Application Filed stage onwards.';
    if (!values.refundApplicationDate) errors.refundApplicationDate = 'Application filing date is required from Application Filed stage onwards.';
    if (!String(values.refundArn || '').trim()) errors.refundArn = 'ARN is required from Application Filed stage onwards.';
  }
  if (!values.templateId && values.currentStage && !isStageCompatibleWithLaw(values.currentStage, values.lawType, values.matterTemplate)) errors.currentStage = 'The selected stage does not belong to the selected law type.';
  if (!MATTER_STATUSES.includes(values.status)) errors.status = 'Select a valid status.';
  if (ACTIVE_MATTER_STATUSES.includes(values.status)) {
    if (!values.nextAction?.trim()) errors.nextAction = 'Next action is required for an active matter.';
    if (!values.nextActionDate) errors.nextActionDate = 'Next action date is required for an active matter.';
  }
  if (values.status === 'Closed') {
    if (!values.closureDate) errors.closureDate = 'Closure date is required.';
    if (!values.finalOutcome?.trim()) errors.finalOutcome = 'Final outcome is required.';
    if (!values.closureRemarks?.trim()) errors.closureRemarks = 'Closure remarks are required.';
  }
  return errors;
}

export function nextActionDate(matter) {
  return matter?.nextActionDate || matter?.nextHearingDate || matter?.hearingDate || matter?.replyDueDate || matter?.appealDueDate || null;
}

export function isHearingEvent(type) {
  return ['Hearing Notice Issued','Hearing Scheduled','Hearing Attended','Hearing Not Attended','Adjournment Requested','Adjournment Granted','Next Hearing Fixed'].includes(type);
}

export function isOrderEvent(type) { return type === 'Order Passed'; }
export function isFilingEvent(type) {
  return ['Reply Filed','Additional Evidence Filed','Additional Documents Filed','Paper Book Filed','Written Submission Filed','Appeal Filed'].includes(type);
}


export function matterFieldLabels(lawType) {
  if (lawType === 'Income Tax') {
    return {
      year: 'Assessment Year',
      period: 'Proceeding / Period',
      noticeNumber: 'Notice / Appeal Number',
      authority: 'Income Tax Authority / Forum',
      noticeDate: 'Notice Received Date',
      section: 'Section / Order Type',
    };
  }
  if (lawType === 'GST') {
    return {
      year: 'Financial Year',
      period: 'Tax Period',
      noticeNumber: 'Form / Notice / Appeal Number',
      authority: 'GST Authority / Forum',
      noticeDate: 'Notice Received Date',
      section: 'Form / Section / Order Type',
    };
  }
  return {
    year: 'Financial / Assessment Year',
    period: 'Period',
    noticeNumber: 'Notice / Appeal Number',
    authority: 'Authority / Forum',
    noticeDate: 'Notice Received Date',
    section: 'Section / Order Type',
  };
}

export function caseUpdateValidation(values = {}) {
  const errors = {};
  const type = values.entryType || '';
  if (!values.entryDate) errors.entryDate = 'Date is required.';
  if (!String(values.title || '').trim()) errors.title = 'Short heading is required.';
  if (!values.proceedingStage) errors.proceedingStage = 'Proceeding stage is required.';

  if (type === 'Stage Transition' && !values.transitionToStage) {
    errors.transitionToStage = 'Select the stage to move to.';
  }
  if (type === 'Reply Filed' || type === 'Appeal Filed' || isFilingEvent(type)) {
    if (!values.filingDate) errors.filingDate = 'Filing date is required.';
    if ((type === 'Reply Filed' || type === 'Appeal Filed') && !String(values.acknowledgementNumber || '').trim()) {
      errors.acknowledgementNumber = 'Acknowledgement / reference number is required.';
    }
  }
  if (type === 'Hearing Scheduled' || type === 'Hearing Attended' || type === 'Hearing Not Attended' || type === 'Next Hearing Fixed') {
    if (!values.hearingDate && type !== 'Next Hearing Fixed') errors.hearingDate = 'Hearing date is required.';
    if (type === 'Next Hearing Fixed' && !values.nextHearingDate) errors.nextHearingDate = 'Next hearing date is required.';
  }
  if (type === 'Hearing Attended') {
    if (values.attended !== 'Yes') errors.attended = 'Mark the hearing as attended.';
    if (!String(values.hearingOutcome || '').trim()) errors.hearingOutcome = 'Hearing outcome is required.';
  }
  if (type === 'Hearing Not Attended') {
    if (values.attended !== 'No') errors.attended = 'Mark the hearing as not attended.';
    if (!String(values.description || '').trim() && !String(values.adjournmentReason || '').trim()) {
      errors.description = 'Record why the hearing was not attended.';
    }
  }
  if (type === 'Adjournment Requested') {
    if (!String(values.adjournmentReason || '').trim()) errors.adjournmentReason = 'Adjournment reason is required.';
    if (!values.nextActionDate) errors.nextActionDate = 'Next action date is required.';
  }
  if (type === 'Order Passed') {
    if (!values.orderDate) errors.orderDate = 'Order date is required.';
    if (!values.orderOutcome) errors.orderOutcome = 'Order outcome is required.';
    if (!values.appealRequired) errors.appealRequired = 'Select whether appeal is required or under review.';
    if (values.appealRequired === 'Yes' && !values.appealDueDate) errors.appealDueDate = 'Appeal due date is required.';
  }
  if (values.updatePresentStatus && ACTIVE_MATTER_STATUSES.includes(values.matterStatus || 'Open')) {
    if (!String(values.nextAction || '').trim()) errors.nextAction = 'Next action is required when updating an active matter.';
    if (!values.nextActionDate) errors.nextActionDate = 'Next action date is required when updating an active matter.';
  }
  return errors;
}

function localDay(value) {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0,0,0,0);
  return date;
}

export function matterAttention(matter, todayValue = new Date()) {
  const today = localDay(todayValue);
  const active = !['Closed','Archived'].includes(matter?.status);
  if (!active) return { key: 'none', label: '', className: '' };

  const dates = [
    ['Hearing Today', matter?.nextHearingDate, 0, 'bg-red-100 text-red-800'],
    ['Reply Due', matter?.replyDueDate, 3, 'bg-orange-100 text-orange-800'],
    ['Appeal Due', matter?.appealDueDate, 7, 'bg-orange-100 text-orange-800'],
    ['Next Action', matter?.nextActionDate, 7, 'bg-yellow-100 text-yellow-800'],
  ];
  for (const [label, value, soonDays, className] of dates) {
    const d = localDay(value);
    if (!d) continue;
    const diff = Math.round((d - today) / 86400000);
    if (diff < 0) return { key: 'overdue', label: `${label} overdue`, className: 'bg-red-100 text-red-800' };
    if (diff === 0) return { key: 'today', label, className };
    if (diff <= soonDays) return { key: 'soon', label: `${label} in ${diff} day${diff===1?'':'s'}`, className };
  }
  if (!matter?.nextAction || !matter?.nextActionDate) {
    return { key: 'missing', label: 'No next action', className: 'bg-gray-200 text-gray-800' };
  }
  return { key: 'none', label: '', className: '' };
}

export function stageTransitionWarning(currentStage, nextStage, lawType) {
  if (!currentStage || !nextStage || currentStage === nextStage) return '';
  const stages = stagesForLaw(lawType);
  const currentIndex = stages.indexOf(currentStage);
  const nextIndex = stages.indexOf(nextStage);
  if (currentIndex >= 0 && nextIndex >= 0 && nextIndex < currentIndex) {
    return `This moves the proceeding backward from "${currentStage}" to "${nextStage}". Continue only if this is intentional.`;
  }
  if (currentIndex >= 0 && nextIndex > currentIndex + 1) {
    return `This skips one or more normal stages between "${currentStage}" and "${nextStage}". Continue only if this is intentional.`;
  }
  return '';
}
